import { useQuery } from '@tanstack/react-query';

import { accountApi } from '@/api/commercial/accounts';
import { CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import { creditAnalysisDocumentReviewApi } from '@/api/commercial/credit-analysis-document-reviews';
import { accountUploadApi } from '@/api/upload/account-uploads';
import { DocumentsUploadFormTabAnalysis } from '@/components/forms/commercial/credit-analysis/document-upload-form-tab-analysis';
import { CreditAnalysisUpdateForm } from '@/components/forms/commercial/credit-analysis/update';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import type { AccountType } from '@/hooks/commercial/use-credit-analysis-state-machine';
import {
  fieldsByAccountType,
  normalizeAccountTypeForCreditAnalysis,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import type { CreditAnalysisTabProps } from './general-tab';

export function CreditAnalysisAnalysisTab({ creditAnalysis }: CreditAnalysisTabProps) {
  const { data: account } = useQuery({
    queryKey: ['credit-analysis-analysis', creditAnalysis?.id],
    queryFn: () => accountApi.findOne(creditAnalysis?.accountId!),
    enabled: !!creditAnalysis?.accountId,
  });

  const { data: accountDocuments } = useQuery({
    queryKey: ['accountDocuments', creditAnalysis?.accountId],
    queryFn: () => accountApi.findDocuments(creditAnalysis?.accountId!),
    enabled: !!creditAnalysis?.accountId,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['accountContacts', creditAnalysis?.accountId],
    queryFn: () => accountApi.findContacts(creditAnalysis?.accountId!),
    enabled: !!creditAnalysis?.accountId,
  });

  const isAnalysisCompleted = creditAnalysis?.status
    ? [
        CreditAnalysisStatus.APPROVED,
        CreditAnalysisStatus.PARTIALLY_APPROVED,
        CreditAnalysisStatus.DECLINED,
        CreditAnalysisStatus.DOC_DECLINED,
      ].includes(creditAnalysis.status)
    : false;

  const allEmails: string[] = [
    ...(accountDocuments?.subjects?.map((s) => s.value) || []),
    ...(accountDocuments?.externalSubjects?.map((e) => e.value) || []),
  ];

  const normalizedAccountType = normalizeAccountTypeForCreditAnalysis(
    account?.accountType,
    account?.simeiSince,
    account?.document?.type,
  );

  const { data: accountUploadsResponse } = useQuery({
    queryKey: ['account-uploads', account?.id, 'credit-analysis'],
    queryFn: () => accountUploadApi.list(String(account?.id), 'credit-analysis'),
    enabled: !!account?.id,
  });

  const accountUploads = accountUploadsResponse?.docs ?? [];

  const { data: documentReviews = [] } = useQuery({
    queryKey: ['credit-analysis-document-reviews', creditAnalysis?.id],
    queryFn: async () => {
      if (!creditAnalysis?.id || !account?.id) {
        return [];
      }
      return creditAnalysisDocumentReviewApi.sync({
        creditAnalysisId: creditAnalysis.id,
        accountId: account.id,
      });
    },
    enabled: !!creditAnalysis?.id && !!account?.id,
    staleTime: 60 * 1000,
  });

  const requiredFields = (fieldsByAccountType[normalizedAccountType as AccountType] || [])
    .filter((field) => field.required)
    .map((field) => field.key);

  const uploadedFields = accountUploads.map((upload) => upload.fieldId).filter(Boolean);
  const allRequiredDocsUploaded = requiredFields.every((field) => uploadedFields.includes(field));

  const documentReviewsMap = new Map(documentReviews.map((r) => [r.accountUploadId, r]));
  const allRequiredDocsReviewed = requiredFields.every((fieldKey) => {
    const upload = accountUploads.find((u) => u.fieldId === fieldKey);
    if (!upload) return false; // No upload for this required field
    const review = documentReviewsMap.get(upload.id);
    // Only approved documents count - rejected documents prevent analysis
    return review && review.status === 'approved';
  });

  const isWaitingForClientCorrection =
    creditAnalysis?.status === CreditAnalysisStatus.PENDING_EXTERNAL_DOCS;

  return (
    <div className="space-y-6">
      <Card>
        <CardTitle className="px-5 pt-6">
          Documentos
          {isWaitingForClientCorrection && ' ⚠️'}
        </CardTitle>
        <CardContent className="pt-6">
          {account ? (
            <DocumentsUploadFormTabAnalysis
              creditAnalysis={creditAnalysis}
              proposalId={creditAnalysis?.proposalId!}
              account={account}
              accountType={normalizedAccountType}
              contacts={contacts}
              recipients={allEmails}
            />
          ) : (
            <p>Carregando...</p>
          )}
        </CardContent>
      </Card>

      {!isAnalysisCompleted && (
        <CreditAnalysisUpdateForm
          creditAnalysis={creditAnalysis}
          allDocumentsReviewed={allRequiredDocsUploaded && allRequiredDocsReviewed}
          onSuccess={() => {}}
        />
      )}

      {isAnalysisCompleted && (
        <Card>
          <CardTitle className="px-5 pt-6">Análise</CardTitle>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              Esta análise de crédito já foi{' '}
              {creditAnalysis?.status === CreditAnalysisStatus.APPROVED ||
              creditAnalysis?.status === CreditAnalysisStatus.PARTIALLY_APPROVED
                ? 'aprovada'
                : 'reprovada'}
              .
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
